import { useState } from 'react'
import CheckIcon from '@/shared/assets/icons/conversion/checkIcon.svg?react'
import CrossIcon from '@/shared/assets/icons/cross.svg?react'
import { useCallbacksListQuery } from '@/entities/callback/api'
import { UniversalCallbackAdminProfile } from '@/entities/callback/model'
import { ColumnDef } from '@tanstack/react-table'

import { TopupStatus, TopupStatusIcons, TopupStatusTitles } from '@/entities/topups'
import { DealIcon, ItemLayout } from '@/shared/components'
import { formatCurrency, formatDate } from '@/shared/lib'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { DatePickerWithRange } from '@/entities/datePicker'
import { DateRange } from 'react-day-picker'
import { SearchInput } from '@/shared/components/SearchInput/SearchInput.tsx'
import { BaseTable } from 'syncra-storybook/features'
import { BasePagination } from 'syncra-storybook/shared/components'

type Props = {
  requisitId?: string
}

export const CallbackList = ({ requisitId }: Props) => {
  const [skip, setSkip] = useState(0)
  const [search, setSearch] = useState('')
  const [take] = useState(DEFAULT_TAKE)
  const [dateRange, setDateRange] = useState<{
    startDate?: Date
    endDate?: Date
  }>({})

  const { callbacksData, callbacksError, callbacksIsLoading } = useCallbacksListQuery({
    requisitId,
    skip,
    take,
    anySearch: search,
    dateFrom: dateRange.startDate?.toISOString(),
    dateTo: dateRange.endDate?.toISOString()
  })

  const handleDateChange = (range: DateRange | undefined) => {
    if (!range) {
      setDateRange({})
    } else {
      setDateRange({
        startDate: range.from,
        endDate: range.to,
      })
    }
    setSkip(0) // сброс пагинации при изменении дат
  }

  const handlePageChange = (newSkip: number) => {
    setSkip(newSkip)
  }


  // Лоадер
  if (callbacksIsLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  // Ошибка
  if (callbacksError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-red-800 font-medium">Ошибка загрузки данных</h3>
            <p className="text-red-600 text-sm mt-1">
              {callbacksError instanceof Error
                ? callbacksError.message
                : 'Не удалось загрузить список колбэков'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Определяем колонки
  const columns: ColumnDef<UniversalCallbackAdminProfile>[] = [
    {
      accessorKey: 'createdAt',
      header: 'Дата и время',
      cell: ({ getValue }) => {
        const date = getValue() as string
        return formatDate(date,'withoutSeconds')
      }
    },
    {
      accessorKey: 'isSuccessProcessed',
      header: 'Статус',
      cell: ({ getValue }) => {
        const isSuccess = getValue() as boolean | null
        return (
          <div className="flex justify-center">
            {isSuccess ? (
              <CheckIcon className="w-5 h-5 text-green-500" />
            ) : (
              <CrossIcon className="w-5 h-5 text-[#D92D20]" />
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'callbackMessage',
      header: 'Текст',
      cell: ({ getValue }) => (getValue() as string) || '—'
    },
    {
      accessorKey: 'callbackId',
      header: 'ID',
      cell: ({ getValue }) => (
        <div className="text-[#5959FF]">{(getValue() as string) || '—'}</div>
      )
    },
    {
      accessorKey: 'deal',
      header: 'Сделка',
      cell: ({ row }) => {
        if (!row.original.topup) return <div className="text-gray-400">—</div>
        const createdAt = row.original.topup?.createdAt || ''
        const status = row.original.topup?.topupStatus || TopupStatus.Unknown
        const title = TopupStatusTitles[status]
        const iconStatus = TopupStatusIcons[status]
        return (
          <ItemLayout
            icon={<DealIcon status={iconStatus} />}
            subtitle={new Date(createdAt).toLocaleString('ru-RU')}
            title={title}
          />
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Сумма',
      cell: ({ row }) => {
        if (!row.original.topup) return <div className="text-gray-400">—</div>
        const amountFrom =
          row.original.topup?.factAmountFrom && row.original.topup?.factAmountFrom !== 0
            ? row.original.topup?.factAmountFrom
            : row.original.payAmountFrom
        const amountTo =
          row.original.topup?.factAmountTo && row.original.topup?.factAmountTo !== 0
            ? row.original.topup?.factAmountTo
            : row.original.topup?.amountTo
        const currencyFrom = row.original.topup?.currencyFrom
        const currencyTo = row.original.topup?.currencyTo
        return (
          <div className="flex flex-col">
            <span>{formatCurrency(amountTo, currencyTo)}</span>
            <span className="text-text-secondary">{formatCurrency(amountFrom, currencyFrom)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'receiverBank',
      header: 'Платежный метод',
      cell: ({ getValue }) => (
        <div className="text-[#5959FF]">{(getValue() as string) || '—'}</div>
      )
    }
  ]

  return (
    <div className="space-y-4">

      {/* Таблица */}
      <BaseTable
        columns={columns}
        data={callbacksData?callbacksData.items:[]}
        forceDesktopTable={true}
        title="Сообщения"
        filterContent={
          <div className="flex justify-between items-center mb-4 gap-4">
            <DatePickerWithRange
              onDateChange={handleDateChange}
            />

            <SearchInput onChange={(e)=>{setSearch(e.target.value)}} />

          </div>
      }
        skip={skip}
        take={take}
        totalItems={callbacksData?callbacksData.totalCount:0}
        onPageChange={handlePageChange}
        cardPadding="md"
        cardVariant="primary"
      />

      {/* Пагинация */}
      {callbacksData && callbacksData.totalCount > take && (
        <div className="flex justify-center">
          <BasePagination
            skip={skip}
            take={take}
            totalItems={callbacksData.totalCount}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  )
}
